import React, { useState, useCallback, useEffect } from 'react';

// Inline SVG flag — Windows doesn't render regional indicator emojis (🇫🇷)
const FrFlag = ({ className = 'w-4 h-4' }: { className?: string }) => (
    <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg" aria-label="France">
        <rect width="1" height="2" fill="#002395" />
        <rect x="1" width="1" height="2" fill="#fff" />
        <rect x="2" width="1" height="2" fill="#ED2939" />
    </svg>
);
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Check, Loader2, Lock, Copy, Twitter, MessageCircle, Sparkles, Trophy, Zap, Flame, Target, Gem, Users, Linkedin, Mail, Send, Facebook, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
// Firebase Firestore imports removed — signup now goes through backend API
// to enable server-side referral notifications
import WaitlistHeader from './WaitlistHeader';
import WaitlistFooter from './WaitlistFooter';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import WaitlistFeatureShowcase from './WaitlistFeatureShowcase';
import WaitlistDeepDive from './WaitlistDeepDive';
import WaitlistBottomSections from './WaitlistBottomSections';

type FormStatus = 'idle' | 'loading' | 'success' | 'duplicate' | 'error';

import { REFERRAL_TIERS } from '../config/referral-tiers';
import type { ReferralTier } from '../config/referral-tiers';

// Icon mapping for tier display (UI-only, not needed in shared config)
const TIER_ICONS: Record<string, React.ComponentType<any>> = {
    pioneer: Trophy,
    accelerator: Zap,
    amplifier: Flame,
    vanguard: Target,
    founding: Gem,
};

const WaitlistPage: React.FC = () => {
    const { t } = useTranslation('marketing');
    const [email, setEmail] = useState('');
    const [gdprConsent, setGdprConsent] = useState(false);
    const [status, setStatus] = useState<FormStatus>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [cohortPosition, setCohortPosition] = useState<number | null>(null);
    const [referralCode, setReferralCode] = useState('');
    const [copied, setCopied] = useState(false);
    const [inboundRef, setInboundRef] = useState<string | null>(null);
    const [showRefInput, setShowRefInput] = useState(false);
    const [referralCount, setReferralCount] = useState(0);
    const [heroIndex, setHeroIndex] = useState(0);
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Hero phrases for the carousel (from translations)
    const heroPhrases: string[] = t('waitlist.hero_phrases', { returnObjects: true }) as string[];

    // Generate a deterministic referral code from email (FNV-1a hash)
    const generateReferralCode = (email: string) => {
        let hash = 2166136261;
        for (let i = 0; i < email.length; i++) {
            hash ^= email.charCodeAt(i);
            hash = (hash * 16777619) >>> 0;
        }
        return hash.toString(36).toUpperCase().padStart(8, '0').substring(0, 8);
    };

    // Read ?ref= param from URL on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref) {
            setInboundRef(ref);
            setShowRefInput(true);
        }
    }, []);

    // Auto-advance hero phrase every 3 seconds
    useEffect(() => {
        if (!heroPhrases || heroPhrases.length <= 1) return;
        const timer = setInterval(() => {
            setHeroIndex((i) => (i + 1) % heroPhrases.length);
        }, 4500);
        return () => clearInterval(timer);
    }, [heroPhrases.length]);

    // Compute current tier and progress toward next tier
    const currentTierIndex = REFERRAL_TIERS.reduce((acc, tier, i) => referralCount >= tier.count ? i : acc, -1);
    const nextTier = REFERRAL_TIERS[currentTierIndex + 1] ?? null;
    const prevTierCount = currentTierIndex >= 0 ? REFERRAL_TIERS[currentTierIndex].count : 0;
    const progressPct = nextTier
        ? Math.min(((referralCount - prevTierCount) / (nextTier.count - prevTierCount)) * 100, 100)
        : 100;

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = email.trim().toLowerCase();

        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setStatus('error');
            setErrorMsg(t('hero.waitlist_invalid_email'));
            return;
        }

        if (!gdprConsent) {
            setStatus('error');
            setErrorMsg(t('waitlist.gdpr_consent'));
            return;
        }

        setStatus('loading');

        try {
            // Capture reCAPTCHA token (with 3s timeout — empty/invalid keys cause hang)
            let captchaToken: string | null = null;
            if (executeRecaptcha) {
                try {
                    const recaptchaPromise = executeRecaptcha('waitlist_signup');
                    const timeoutPromise = new Promise<string>((_, reject) =>
                        setTimeout(() => reject(new Error('reCAPTCHA timeout')), 3000)
                    );
                    captchaToken = await Promise.race([recaptchaPromise, timeoutPromise]);
                } catch (recaptchaErr) {
                    console.warn('reCAPTCHA failed/timed out, proceeding without token:', recaptchaErr);
                }
            }

            // Try backend API first (enables server-side referral notifications)
            const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
            let apiSuccess = false;

            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 12000); // 12s timeout

                const response = await fetch(`${backendUrl}/api/v1/waitlist/signup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: trimmed,
                        referralCode: inboundRef || undefined,
                        captchaToken,
                    }),
                    signal: controller.signal,
                });
                clearTimeout(timeout);

                const data = await response.json();

                if (response.ok) {
                    apiSuccess = true;
                    setReferralCode(data.referralCode);
                    setCohortPosition(data.position);
                    setReferralCount(data.referralCount || 0);
                    setStatus(data.isDuplicate ? 'duplicate' : 'success');
                } else {
                    console.warn('Backend API returned error, falling back to direct Firestore:', data);
                }
            } catch (apiErr) {
                console.warn('Backend API unreachable, falling back to direct Firestore:', apiErr);
            }

            // Fallback: write directly to Firestore (client SDK)
            if (!apiSuccess) {
                const { collection, query, where, getDocs, addDoc, serverTimestamp } = await import('firebase/firestore');
                const firebaseLib = await import('../lib/firebase');
                const db = firebaseLib.db;
                const waitlistRef = collection(db, 'waitlist');

                // Check for duplicate
                const existing = await getDocs(query(waitlistRef, where('email', '==', trimmed)));
                const code = generateReferralCode(trimmed);

                if (!existing.empty) {
                    setReferralCode(code);
                    setCohortPosition(existing.size); // approximate position
                    setStatus('duplicate');
                    return;
                }

                // Write new signup
                await addDoc(waitlistRef, {
                    email: trimmed,
                    createdAt: serverTimestamp(),
                    source: 'waitlist-page-fallback',
                    gdprConsent: true,
                    ...(inboundRef ? { referredBy: inboundRef } : {}),
                });

                // Get approximate position
                const { getCountFromServer } = await import('firebase/firestore');
                const countSnap = await getCountFromServer(waitlistRef);
                const position = countSnap.data().count;

                setReferralCode(code);
                setCohortPosition(position);
                setStatus('success');
            }
        } catch (err) {
            console.error('Waitlist signup failed:', err);
            setStatus('error');
            setErrorMsg(t('waitlist.generic_error'));
        }
    }, [email, gdprConsent, executeRecaptcha, t, inboundRef]);


    return (
        <div
            className="min-h-screen font-inter flex flex-col relative overflow-hidden selection:bg-hig-accent/30 bg-hig-bg-primary"
        >
            {/* === Background — identical to App.tsx main site === */}
            <div className="fixed inset-0 z-0 overflow-hidden bg-hig-bg-primary">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:opacity-100 opacity-0"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 dark:opacity-0 opacity-100"></div>
                <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIzMDAiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMSIvPjwvc3ZnPg==')]" />
            </div>

            <WaitlistHeader />

            <main className="flex-1 relative z-10">

                {/* ═══════════ HERO ═══════════ */}
                <section className="relative pt-24 md:pt-32 pb-14" id="hero">
                    <div className="container-hig relative">
                        <div className="max-w-3xl mx-auto text-center">

                            {/* France + EU AI Act origin badges */}
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45 }}
                                className="flex items-center justify-center gap-2 flex-wrap mb-4"
                            >
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(96,165,250,0.4)', background: 'rgba(96,165,250,0.10)', color: '#93c5fd' }}>
                                    <FrFlag className="w-3.5 h-3.5" /> Made in France
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.10)', color: '#fbbf24' }}>
                                    <span>⚖️</span> EU AI Act Ready
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(52,211,153,0.4)', background: 'rgba(52,211,153,0.10)', color: '#6ee7b7' }}>
                                    <span>🔐</span> GDPR by Design
                                </span>
                            </motion.div>

                            {/* Eyebrow */}
                            <motion.p
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="text-[11px] font-bold tracking-[0.2em] uppercase text-hig-accent/60 mb-6"
                            >
                                {t('waitlist.eyebrow')}
                            </motion.p>

                            {/* H1 — ownership statement with carousel */}
                            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-semibold text-hig-text-primary mb-5 lg:mb-7 tracking-tight leading-[1.08] h-[2.16em] flex items-center justify-center">
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={heroIndex}
                                        initial={{ opacity: 0, y: 18 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -18 }}
                                        transition={{ duration: 0.45, ease: 'easeInOut' }}
                                        className="block"
                                    >
                                        {Array.isArray(heroPhrases) && heroPhrases.length > 0
                                            ? heroPhrases[heroIndex]
                                            : t('waitlist.hero_headline')}
                                    </motion.span>
                                </AnimatePresence>
                            </h1>

                            {/* Subheadline — what it does + supporting line */}
                            <div className="max-w-xl lg:max-w-2xl mx-auto mb-5 lg:mb-8 space-y-3">
                                <p className="text-base md:text-lg lg:text-xl text-hig-text-secondary leading-relaxed">
                                    {t('waitlist.hero_sub')}
                                </p>
                                <p className="text-sm text-hig-text-tertiary leading-relaxed">
                                    {t('waitlist.hero_supporting')}
                                </p>
                            </div>

                            {/* Trust strip */}
                            <div className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-hig-border bg-hig-bg-tertiary/30 mb-8 lg:mb-12">
                                <p className="text-[10px] md:text-[11px] lg:text-xs text-hig-text-tertiary tracking-wide">
                                    {t('waitlist.trust_strip')}
                                </p>
                            </div>

                            {/* ═══ CTA BLOCK ═══ */}
                            <div className="max-w-md lg:max-w-lg xl:max-w-xl mx-auto">
                                <AnimatePresence mode="wait">
                                    {status === 'success' || status === 'duplicate' ? (
                                        <motion.div
                                            key="success-state"
                                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            className="referral-panel relative rounded-2xl border border-hig-border overflow-hidden"
                                            style={{ background: 'var(--referral-panel-bg, linear-gradient(180deg, rgba(15,15,26,0.97) 0%, rgba(10,10,20,0.99) 100%))' }}
                                        >
                                            {/* Animated top accent line */}
                                            <div className="absolute top-0 inset-x-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #34d399, #4f46e5, #a78bfa, #e879f9, #fbbf24)' }} />
                                            {/* Inject theme-aware CSS variables */}
                                            <style>{`
                                                .referral-panel { --referral-panel-bg: linear-gradient(180deg, rgba(15,15,26,0.97) 0%, rgba(10,10,20,0.99) 100%); --rp-text: #ffffff; --rp-text-sec: rgba(255,255,255,0.6); --rp-text-dim: rgba(255,255,255,0.4); --rp-text-faint: rgba(255,255,255,0.3); --rp-text-ghost: rgba(255,255,255,0.15); --rp-surface: rgba(255,255,255,0.03); --rp-surface-hover: rgba(255,255,255,0.06); --rp-border: rgba(255,255,255,0.06); --rp-bar-bg: rgba(255,255,255,0.04); --rp-input-bg: rgba(15,23,42,0.7); --rp-checkmark-border: #0f0f1a; --rp-label-color: #e2e8f0; }
                                                .light .referral-panel { --referral-panel-bg: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,248,250,0.99) 100%); --rp-text: #1D1D1F; --rp-text-sec: rgba(0,0,0,0.55); --rp-text-dim: rgba(0,0,0,0.4); --rp-text-faint: rgba(0,0,0,0.25); --rp-text-ghost: rgba(0,0,0,0.12); --rp-surface: rgba(0,0,0,0.03); --rp-surface-hover: rgba(0,0,0,0.06); --rp-border: rgba(0,0,0,0.08); --rp-bar-bg: rgba(0,0,0,0.06); --rp-input-bg: rgba(0,0,0,0.04); --rp-checkmark-border: #ffffff; --rp-label-color: #1D1D1F; }
                                            `}</style>
                                            
                                            {/* ─── HERO BANNER ─── */}
                                            <div className="relative px-6 pt-8 pb-6 text-center" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(79,70,229,0.15) 0%, transparent 70%)' }}>
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                                                    className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                                                    style={{
                                                        background: status === 'duplicate'
                                                            ? 'linear-gradient(135deg, rgba(79,70,229,0.2), rgba(79,70,229,0.05))'
                                                            : 'linear-gradient(135deg, rgba(52,211,153,0.2), rgba(52,211,153,0.05))',
                                                        border: status === 'duplicate'
                                                            ? '2px solid rgba(79,70,229,0.4)'
                                                            : '2px solid rgba(52,211,153,0.4)',
                                                    }}
                                                >
                                                    {status === 'duplicate' ? (
                                                        <span className="text-2xl">👋</span>
                                                    ) : (
                                                        <Check className="w-7 h-7 text-green-400" />
                                                    )}
                                                </motion.div>
                                                <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--rp-text)' }}>
                                                    {status === 'duplicate' ? t('waitlist.welcome_back_title') : t('waitlist.success_reserved')}
                                                </h3>
                                                {cohortPosition && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 0.3 }}
                                                    >
                                                        <p className="text-sm text-hig-text-secondary mb-3">
                                                            {status === 'duplicate'
                                                                ? t('waitlist.welcome_back_subtitle')
                                                                : t('waitlist.success_cohort')}
                                                        </p>
                                                        <span
                                                            className="inline-block text-4xl font-black tracking-tight"
                                                            style={{ background: 'linear-gradient(135deg, #4f46e5, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                                                        >
                                                            HR-{((cohortPosition * 2749 + 58391) % 99991 + 10000).toLocaleString()}
                                                        </span>
                                                    </motion.div>
                                                )}
                                                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase"
                                                    style={{ background: 'rgba(79,70,229,0.15)', border: '1px solid rgba(79,70,229,0.3)', color: '#818cf8' }}>
                                                    <Sparkles className="w-3 h-3" />
                                                    {t('waitlist.success_phase_badge', 'Phase 1')}
                                                </div>
                                            </div>

                                            <div className="px-6 pb-6 space-y-5">
                                                {/* ─── REFERRAL CODE + SHARE ─── */}
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.5 }}
                                                    className="relative rounded-xl p-5 text-center"
                                                    style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.12) 0%, rgba(124,58,237,0.08) 100%)', border: '1px solid rgba(79,70,229,0.2)' }}
                                                >
                                                    <p className="text-xs text-hig-text-tertiary uppercase tracking-wider font-medium mb-3">
                                                        🔗 {t('waitlist.success_referral_link_label', 'Your referral link')}
                                                    </p>
                                                    <div className="flex items-center gap-2 p-1.5 rounded-xl mb-4" style={{ background: 'var(--rp-bar-bg)', border: '1px solid var(--rp-border)' }}>
                                                        <div className="flex-1 px-3 py-2.5 text-sm text-hig-text-secondary font-mono tracking-tight overflow-hidden text-ellipsis whitespace-nowrap">
                                                            humanreplica.ai/?ref={referralCode}
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(`https://humanreplica.ai/?ref=${referralCode}`);
                                                                setCopied(true);
                                                                setTimeout(() => setCopied(false), 2000);
                                                            }}
                                                            title="Copy link"
                                                            className="flex items-center justify-center w-10 h-10 rounded-lg transition-all text-white"
                                                            style={{ background: copied ? 'rgba(52,211,153,0.2)' : 'rgba(79,70,229,0.3)', border: `1px solid ${copied ? 'rgba(52,211,153,0.4)' : 'rgba(79,70,229,0.4)'}` }}
                                                        >
                                                            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                    <p className="text-[10px] text-hig-text-tertiary mb-2">{t('waitlist.share_with_network')}</p>
                                                    <div className="grid grid-cols-4 gap-2">
                                                        {/* X / Twitter */}
                                                        <a
                                                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(t('waitlist.share_text_twitter'))}&url=${encodeURIComponent(`https://humanreplica.ai/?ref=${referralCode}`)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Share on X"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
                                                            style={{ background: 'rgba(29,155,240,0.1)', border: '1px solid rgba(29,155,240,0.2)', color: '#1d9bf0' }}
                                                        >
                                                            <Twitter className="w-4 h-4" />
                                                            X
                                                        </a>
                                                        {/* LinkedIn */}
                                                        <a
                                                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://humanreplica.ai/?ref=${referralCode}`)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Share on LinkedIn"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
                                                            style={{ background: 'rgba(10,102,194,0.1)', border: '1px solid rgba(10,102,194,0.2)', color: '#0a66c2' }}
                                                        >
                                                            <Linkedin className="w-4 h-4" />
                                                            LinkedIn
                                                        </a>
                                                        {/* WhatsApp */}
                                                        <a
                                                            href={`https://wa.me/?text=${encodeURIComponent(`${t('waitlist.share_text_whatsapp')} https://humanreplica.ai/?ref=${referralCode}`)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Share on WhatsApp"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
                                                            style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.2)', color: '#25d366' }}
                                                        >
                                                            <MessageCircle className="w-4 h-4" />
                                                            WhatsApp
                                                        </a>
                                                        {/* Telegram */}
                                                        <a
                                                            href={`https://t.me/share/url?url=${encodeURIComponent(`https://humanreplica.ai/?ref=${referralCode}`)}&text=${encodeURIComponent(t('waitlist.share_text_native'))}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Share on Telegram"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
                                                            style={{ background: 'rgba(38,165,222,0.1)', border: '1px solid rgba(38,165,222,0.2)', color: '#26a5de' }}
                                                        >
                                                            <Send className="w-4 h-4" />
                                                            Telegram
                                                        </a>
                                                        {/* Email */}
                                                        <a
                                                            href={`mailto:?subject=${encodeURIComponent(t('waitlist.share_text_email_subject'))}&body=${encodeURIComponent(`${t('waitlist.share_text_email_body')}\nhttps://humanreplica.ai/?ref=${referralCode}`)}`}
                                                            title="Share via Email"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
                                                            style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)', color: '#eab308' }}
                                                        >
                                                            <Mail className="w-4 h-4" />
                                                            Email
                                                        </a>
                                                        {/* Facebook */}
                                                        <a
                                                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://humanreplica.ai/?ref=${referralCode}`)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Share on Facebook"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
                                                            style={{ background: 'rgba(24,119,242,0.1)', border: '1px solid rgba(24,119,242,0.2)', color: '#1877f2' }}
                                                        >
                                                            <Facebook className="w-4 h-4" />
                                                            Facebook
                                                        </a>
                                                        {/* Reddit */}
                                                        <a
                                                            href={`https://reddit.com/submit?url=${encodeURIComponent(`https://humanreplica.ai/?ref=${referralCode}`)}&title=${encodeURIComponent(t('waitlist.share_text_reddit'))}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Share on Reddit"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
                                                            style={{ background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.2)', color: '#ff4500' }}
                                                        >
                                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 0-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
                                                            Reddit
                                                        </a>
                                                        {/* Native Share (mobile-friendly) */}
                                                        <button
                                                            onClick={() => {
                                                                if (navigator.share) {
                                                                    navigator.share({
                                                                        title: t('waitlist.share_text_email_subject'),
                                                                        text: t('waitlist.share_text_native'),
                                                                        url: `https://humanreplica.ai/?ref=${referralCode}`,
                                                                    }).catch(() => {});
                                                                } else {
                                                                    navigator.clipboard.writeText(`https://humanreplica.ai/?ref=${referralCode}`);
                                                                    setCopied(true);
                                                                    setTimeout(() => setCopied(false), 2000);
                                                                }
                                                            }}
                                                            title="More sharing options"
                                                            className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105 cursor-pointer"
                                                            style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', color: '#a855f7' }}
                                                        >
                                                            <Share2 className="w-4 h-4" />
                                                            {t('waitlist.share_more')}
                                                        </button>
                                                    </div>
                                                </motion.div>

                                                {/* ─── PROGRESS BAR ─── */}
                                                {nextTier && (
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: 0.6 }}
                                                        className="space-y-2"
                                                    >
                                                        <div className="flex items-center justify-between text-xs">
                                                            <span className="text-hig-text-tertiary">{t('waitlist.referred_count', { count: referralCount })}</span>
                                                            <span className="font-semibold" style={{ color: nextTier.color }}>
                                                                {t('waitlist.more_to_next_tier', { count: nextTier.count - referralCount, tier: t(`waitlist.tier_${nextTier.id}_label`) })}
                                                            </span>
                                                        </div>
                                                        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--rp-bar-bg)' }}>
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${progressPct}%` }}
                                                                transition={{ duration: 1.2, ease: 'easeOut', delay: 0.7 }}
                                                                className="h-full rounded-full relative"
                                                                style={{ background: `linear-gradient(90deg, ${nextTier.color}80, ${nextTier.color})` }}
                                                            >
                                                                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full animate-pulse" style={{ background: nextTier.color, boxShadow: `0 0 8px ${nextTier.color}` }} />
                                                            </motion.div>
                                                        </div>
                                                    </motion.div>
                                                )}

                                                {/* ─── TIER LADDER ─── */}
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.7 }}
                                                    className="space-y-2"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider font-medium">{t('waitlist.referral_rewards_title')}</p>
                                                        <p className="text-[10px] text-hig-text-tertiary">{t('waitlist.referral_rewards_subtitle')}</p>
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        {REFERRAL_TIERS.map((tier, i) => {
                                                            const unlocked = referralCount >= tier.count;
                                                            const isNext = !unlocked && (i === 0 || referralCount >= REFERRAL_TIERS[i - 1].count);
                                                            const IconComp = TIER_ICONS[tier.id] || Trophy;
                                                            return (
                                                                <motion.div
                                                                    key={tier.id}
                                                                    initial={{ opacity: 0, x: -10 }}
                                                                    animate={{ opacity: 1, x: 0 }}
                                                                    transition={{ delay: 0.8 + i * 0.08 }}
                                                                    className={`group relative flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                                                        unlocked
                                                                            ? 'border-transparent'
                                                                            : isNext
                                                                                ? 'border-transparent'
                                                                                : 'border-transparent opacity-40'
                                                                    }`}
                                                                    style={{
                                                                        background: unlocked
                                                                            ? `linear-gradient(135deg, ${tier.color}10, ${tier.color}05)`
                                                                            : isNext
                                                                                ? 'var(--rp-surface)'
                                                                                : 'var(--rp-surface)',
                                                                        ...(unlocked && { border: `1px solid ${tier.color}30`, boxShadow: `0 0 20px ${tier.color}10` }),
                                                                        ...(isNext && { border: '1px dashed var(--rp-text-ghost)' }),
                                                                    }}
                                                                >
                                                                    <div
                                                                        className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center relative"
                                                                        style={{
                                                                            background: unlocked ? `${tier.color}20` : isNext ? 'var(--rp-surface-hover)' : 'var(--rp-surface)',
                                                                            border: `1px solid ${unlocked ? tier.color + '40' : 'var(--rp-border)'}`,
                                                                        }}
                                                                    >
                                                                        <IconComp
                                                                            className="w-4 h-4"
                                                                            style={{ color: unlocked ? tier.color : isNext ? 'var(--rp-text-dim)' : 'var(--rp-text-ghost)' }}
                                                                        />
                                                                        {unlocked && (
                                                                            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center border-2" style={{ borderColor: 'var(--rp-checkmark-border)' }}>
                                                                                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <span
                                                                                className="text-xs font-bold"
                                                                                style={{ color: unlocked ? tier.color : isNext ? 'var(--rp-label-color)' : 'var(--rp-text-faint)' }}
                                                                            >
                                                                                {t(`waitlist.tier_${tier.id}_label`)}
                                                                            </span>
                                                                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md"
                                                                                style={{
                                                                                    background: isNext ? `${tier.color}15` : 'var(--rp-bar-bg)',
                                                                                    color: isNext ? tier.color : 'var(--rp-text-faint)',
                                                                                }}
                                                                            >
                                                                                {tier.count === 1 ? t('waitlist.ref_count_one') : t('waitlist.ref_count_other', { count: tier.count })}
                                                                            </span>
                                                                            {isNext && (
                                                                                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full animate-pulse"
                                                                                    style={{ background: `${tier.color}20`, color: tier.color, border: `1px solid ${tier.color}40` }}>
                                                                                    {t('waitlist.tier_next')}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-[11px] leading-tight mt-1 truncate"
                                                                            style={{ color: unlocked ? 'var(--rp-text-sec)' : isNext ? 'var(--rp-text-dim)' : 'var(--rp-text-ghost)' }}>
                                                                            {t(`waitlist.tier_${tier.id}_reward`)}
                                                                        </p>
                                                                    </div>
                                                                    {!unlocked && (
                                                                        <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isNext ? 'var(--rp-text-faint)' : 'var(--rp-text-ghost)' }} />
                                                                    )}
                                                                </motion.div>
                                                            );
                                                        })}
                                                    </div>
                                                </motion.div>

                                                {/* ─── CTA NUDGE ─── */}
                                                <motion.p
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 1.3 }}
                                                    className="text-center text-xs pt-2 pb-1"
                                                    style={{ color: 'var(--rp-text-dim)' }}
                                                >
                                                    <span dangerouslySetInnerHTML={{ __html: t('waitlist.referral_nudge') }} />
                                                </motion.p>
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <div key="form">
                                            {/* Email + CTA form */}
                                            <form
                                                onSubmit={handleSubmit}
                                                className="waitlist-form flex flex-col sm:flex-row gap-3 sm:gap-0 sm:overflow-hidden sm:rounded-full sm:shadow-lg transition-all duration-300"
                                            >
                                                <input
                                                    type="email"
                                                    value={email}
                                                    autoComplete="off"
                                                    onFocus={() => setIsFocused(true)}
                                                    onBlur={() => setIsFocused(false)}
                                                    onChange={(e) => {
                                                        setEmail(e.target.value);
                                                        if (status === 'error') setStatus('idle');
                                                    }}
                                                    placeholder={t('waitlist.email_placeholder')}
                                                    className="waitlist-input flex-1 min-w-0 px-5 py-4 sm:py-3 text-[15px] sm:text-sm rounded-xl sm:rounded-none focus:outline-none"
                                                    style={{
                                                        backgroundColor: 'rgba(15, 23, 42, 0.7)',
                                                        color: '#ffffff',
                                                        caretColor: '#0A84FF',
                                                    }}
                                                    disabled={status === 'loading'}
                                                />
                                                <button
                                                    type="submit"
                                                    disabled={status === 'loading'}
                                                    className="flex items-center justify-center gap-2 px-6 py-4 sm:py-3 rounded-xl sm:rounded-none text-[15px] sm:text-sm font-semibold text-white disabled:opacity-50 transition-opacity"
                                                    style={{ background: 'linear-gradient(135deg, #0A84FF, #0066DD)' }}
                                                >
                                                    {status === 'loading' ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <>
                                                            {t('waitlist.cta')}
                                                            <ArrowRight className="w-3.5 h-3.5" />
                                                        </>
                                                    )}
                                                </button>
                                            </form>

                                            {/* Microcopy under form */}
                                            <p className="text-[11px] sm:text-[11px] text-hig-text-tertiary mt-3 text-center tracking-wide">
                                                {t('waitlist.cta_microcopy')}
                                            </p>

                                            {/* GDPR consent */}
                                            <label className="flex items-start gap-3 mt-4 text-[11px] sm:text-[10px] text-hig-text-tertiary cursor-pointer justify-center leading-relaxed">
                                                <input
                                                    type="checkbox"
                                                    checked={gdprConsent}
                                                    onChange={(e) => setGdprConsent(e.target.checked)}
                                                    className="accent-hig-accent w-4 h-4 sm:w-3 sm:h-3 mt-0.5 flex-shrink-0"
                                                />
                                                <span>{t('waitlist.gdpr_consent')}</span>
                                            </label>

                                            {/* Referral code input — collapsible */}
                                            <div className="mt-3 flex flex-col items-center">
                                                {!showRefInput ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowRefInput(true)}
                                                        className="text-[11px] sm:text-[10px] text-hig-accent hover:text-white transition-colors flex items-center gap-1.5 opacity-80 hover:opacity-100"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                    >
                                                        <Users className="w-3 h-3" />
                                                        {t('waitlist.have_referral_code')}
                                                    </button>
                                                ) : (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        transition={{ duration: 0.2 }}
                                                        className="w-full max-w-xs"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={inboundRef || ''}
                                                                onChange={(e) => setInboundRef(e.target.value.toUpperCase().replace(/\s/g, '') || null)}
                                                                placeholder="e.g. XXXX1234"
                                                                maxLength={12}
                                                                className="flex-1 px-3 py-1.5 text-[12px] sm:text-[11px] rounded-lg focus:outline-none focus:ring-1 focus:ring-hig-accent"
                                                                style={{
                                                                    backgroundColor: 'rgba(15, 23, 42, 0.7)',
                                                                    color: '#ffffff',
                                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                                    fontFamily: 'monospace',
                                                                    letterSpacing: '0.15em',
                                                                    textAlign: 'center',
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => { setInboundRef(null); setShowRefInput(false); }}
                                                                className="text-hig-text-tertiary hover:text-white text-[10px] transition-colors"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                        {inboundRef && (
                                                            <p className="text-[9px] text-emerald-400/70 mt-1 text-center">
                                                                {t('waitlist.referral_code_applied')}
                                                            </p>
                                                        )}
                                                    </motion.div>
                                                )}
                                            </div>

                                            {/* Privacy reassurance */}
                                            <p className="text-[10px] sm:text-[9px] text-hig-text-tertiary mt-2 text-center">
                                                {t('waitlist.privacy_note')}
                                            </p>
                                        </div>
                                    )}
                                </AnimatePresence>

                                {/* Error — subtle, no red to preserve authority */}
                                {status === 'error' && (
                                    <p className="text-hig-text-tertiary text-[10px] mt-2 text-center">
                                        {errorMsg}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </section>


                <WaitlistFeatureShowcase />

                <WaitlistDeepDive />


                <WaitlistBottomSections />
            </main>

            <WaitlistFooter />
        </div>
    );
};

export default WaitlistPage;
